import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import { UserCreate, UserUpdate } from "@api/users";
import { t } from "@lib/i18n";

const userSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters").optional(),
});

type UserFormData = z.infer<typeof userSchema>;

interface UserFormProps {
  initialData?: { name: string; email: string };
  onSubmit: (data: UserCreate | UserUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function UserForm({ initialData, onSubmit, onCancel, isLoading }: UserFormProps) {
  const isEditing = !!initialData;
  
  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: initialData?.name || "",
      email: initialData?.email || "",
      password: "",
    },
  });

  const handleSubmit = (data: UserFormData) => {
    if (initialData) {
      // Update mode - only send changed fields
      const update: UserUpdate = {};
      if (data.name !== initialData.name) update.name = data.name;
      if (data.email !== initialData.email) update.email = data.email;
      if (data.password && data.password.length > 0) update.password = data.password;
      onSubmit(update);
    } else {
      // Create mode - password is required
      if (!data.password || data.password.length < 6) {
        form.setError("password", { message: "Password is required and must be at least 6 characters" });
        return;
      }
      onSubmit({
        name: data.name,
        email: data.email,
        password: data.password,
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("users.nameLabel")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("users.namePlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("users.emailLabel")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("users.emailPlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("users.passwordLabel")}
                {isEditing && <span className="text-muted-foreground ml-1">({t("users.passwordOptional")})</span>}
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={isEditing ? t("users.passwordPlaceholderOptional") : t("users.passwordPlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("common.saving") : isEditing ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

